'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface NavigationItem {
  label: string;
  href: string;
  icon?: string;
}

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  const navigationItems: NavigationItem[] = [
    { label: 'Home', href: '/', icon: 'HomeIcon' },
    { label: 'About Us', href: '/about', icon: 'InformationCircleIcon' },
    { label: 'Services', href: '/services', icon: 'HeartIcon' },
    { label: 'Events', href: '/events', icon: 'CalendarIcon' },
    { label: 'Get Involved', href: '/get-involved', icon: 'UserGroupIcon' },
  ];

  const moreItems: NavigationItem[] = [
    { label: 'Gallery', href: '/gallery', icon: 'PhotoIcon' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActiveLink = (href: string) => {
    return pathname === href;
  };

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass-header shadow-elevated' : 'glass-header'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Link href="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <Image
                  src="/assets/images/logo.png"
                  alt="seva setu logo"
                  width={40}
                  height={40}
                />
              </div>
              <div className="flex flex-col">
                <span className="text-xl lg:text-2xl font-heading font-bold text-primary">
                  Seva Setu Trust
                </span>
                <span className="text-xs text-muted-foreground font-display">
                  सेवा ए परमो धर्म
                </span>
              </div>
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <motion.nav
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.1,
                    delayChildren: 0.2,
                  },
                },
              }}
              className="hidden lg:flex items-center"
            >
              {navigationItems.map((item, index) => (
                <motion.div
                  key={item.href}
                  variants={{
                    hidden: { opacity: 0, y: -20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    href={item.href}
                    className={`px-3 xl:px-4 py-2 rounded-lg font-heading font-medium text-sm transition-all duration-300 ${
                      isActiveLink(item.href)
                        ? 'bg-primary text-primary-foreground shadow-subtle'
                        : 'text-foreground hover:bg-muted hover:text-primary'
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}

              {/* More Dropdown */}
              <motion.div
                className="relative group"
                variants={{
                  hidden: { opacity: 0, y: -20 },
                  visible: { opacity: 1, y: 0 },
                }}
                whileHover={{ scale: 1.05 }}
              >
                <button className="px-4 py-2 rounded-lg font-heading font-medium text-sm text-foreground hover:bg-muted hover:text-primary transition-all duration-300 flex items-center space-x-1">
                  <span>More</span>
                  <motion.div
                    animate={{ rotate: 0 }}
                    whileHover={{ rotate: 180 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Icon name="ChevronDownIcon" size={16} />
                  </motion.div>
                </button>
                <div className="absolute right-0 top-full mt-2 w-48 glass-dropdown rounded-lg shadow-glass opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  {moreItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`flex items-center space-x-3 px-4 py-3 hover:bg-muted transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg ${
                        isActiveLink(item.href) ? 'bg-muted text-primary' : 'text-foreground'
                      }`}
                    >
                      {item.icon && <Icon name={item.icon as any} size={20} />}
                      <span className="font-medium text-sm">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </motion.div>
            </motion.nav>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="hidden lg:flex items-center space-x-3"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  href="/get-involved"
                  className="px-5 py-2.5 rounded-lg font-heading font-semibold text-sm bg-accent text-accent-foreground hover:bg-accent/90 transition-all duration-300 shadow-subtle hover:shadow-cta"
                >
                  Donate Now
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  href="/services"
                  className="px-5 py-2.5 rounded-lg font-heading font-semibold text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-subtle hover:shadow-cta"
                >
                  Get Help
                </Link>
              </motion.div>
            </motion.div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={toggleMobileMenu}
              whileTap={{ scale: 0.9 }}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 glass-overlay"
              onClick={closeMobileMenu}
            />
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="absolute top-16 left-0 right-0 bottom-0 glass-card overflow-y-auto"
            >
              <nav className="container mx-auto px-4 py-6 space-y-2">
                {[...navigationItems, ...moreItems].map((item, index) => (
                  <motion.div
                    key={item.href}
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      href={item.href}
                      onClick={closeMobileMenu}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-heading font-medium transition-all duration-300 ${
                        isActiveLink(item.href)
                          ? 'bg-primary text-primary-foreground shadow-subtle'
                          : 'text-foreground hover:bg-muted hover:text-primary'
                      }`}
                    >
                      {item.icon && <Icon name={item.icon as any} size={20} />}
                      <span>{item.label}</span>
                    </Link>
                  </motion.div>
                ))}

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                  className="pt-4 space-y-3"
                >
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Link
                      href="/get-involved"
                      onClick={closeMobileMenu}
                      className="flex items-center justify-center w-full px-5 py-3 rounded-lg font-heading font-semibold bg-accent text-accent-foreground hover:bg-accent/90 transition-all duration-300 shadow-subtle"
                    >
                      Donate Now
                    </Link>
                  </motion.div>
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Link
                      href="/services"
                      onClick={closeMobileMenu}
                      className="flex items-center justify-center w-full px-5 py-3 rounded-lg font-heading font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-subtle"
                    >
                      Get Help
                    </Link>
                  </motion.div>
                </motion.div>
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;